import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trash2, Plus, Minus, ShoppingBag } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CartPage() {
  const navigate = useNavigate();
  const { token, user, loading: authLoading } = useAuth();
  const { refreshCart } = useCart();
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Wait for auth loading to complete
    if (authLoading) return;
    
    if (!user || !token) {
      toast.error("Please login to view cart");
      navigate("/");
      return;
    }
    fetchCart();
  }, [user, token, authLoading]);

  const fetchCart = async () => {
    try {
      const response = await axios.get(`${API}/cart`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCartItems(response.data.items || []);
    } catch (error) {
      console.error("Failed to fetch cart:", error);
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = async (productId, newQuantity) => {
    try {
      await axios.put(
        `${API}/cart/update`,
        { product_id: productId, quantity: newQuantity },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchCart();
      refreshCart();
    } catch (error) {
      toast.error("Failed to update cart");
    }
  };

  const removeItem = async (productId) => {
    try {
      await axios.delete(`${API}/cart/remove/${productId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Item removed from cart");
      fetchCart();
      refreshCart();
    } catch (error) {
      toast.error("Failed to remove item");
    }
  };

  const calculateTotal = () => {
    return cartItems.reduce((total, item) => {
      const price = item.product.offer_price || item.product.price;
      return total + price * item.quantity;
    }, 0);
  };

  if (loading) {
    return (
      <div data-testid="cart-loading">
        <Navbar />
        <div className="min-h-screen flex items-center justify-center">
          <p className="text-muted-foreground">Loading cart...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="cart-page">
      <Navbar />

      <div className="min-h-screen py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-primary mb-8" data-testid="cart-page-title">
            Shopping Cart
          </h1>

          {cartItems.length === 0 ? (
            <div className="text-center py-20 bg-card rounded-2xl shadow-soft" data-testid="cart-empty">
              <ShoppingBag className="w-20 h-20 text-muted-foreground mx-auto mb-4" />
              <h2 className="font-heading text-2xl font-semibold mb-2">Your cart is empty</h2>
              <p className="text-muted-foreground mb-6">Add some products to get started</p>
              <Button
                onClick={() => navigate("/products")}
                className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-8 py-6"
                data-testid="cart-empty-shop-btn"
              >
                Shop Now
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Cart Items */}
              <div className="lg:col-span-2 space-y-4" data-testid="cart-items">
                {cartItems.map((item) => (
                  <div key={item.product.id} className="bg-card rounded-2xl p-6 shadow-soft" data-testid="cart-item">
                    <div className="flex space-x-4">
                      <div className="w-24 h-24 rounded-xl overflow-hidden bg-muted flex-shrink-0">
                        <img
                          src={item.product.images?.[0] || "https://images.unsplash.com/photo-1564682628523-316b685c5d7b?w=200"}
                          alt={item.product.name}
                          className="w-full h-full object-cover"
                          data-testid="cart-item-image"
                        />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-heading text-xl font-semibold mb-1" data-testid="cart-item-name">
                          {item.product.name}
                        </h3>
                        <p className="text-muted-foreground text-sm mb-3 line-clamp-1">{item.product.description}</p>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <Button
                              onClick={() => updateQuantity(item.product.id, Math.max(1, item.quantity - 1))}
                              variant="outline"
                              size="icon"
                              className="rounded-full w-8 h-8"
                              data-testid="cart-item-decrease-btn"
                            >
                              <Minus className="w-3 h-3" />
                            </Button>
                            <span className="font-bold text-lg w-8 text-center" data-testid="cart-item-quantity">{item.quantity}</span>
                            <Button
                              onClick={() => updateQuantity(item.product.id, item.quantity + 1)}
                              variant="outline"
                              size="icon"
                              className="rounded-full w-8 h-8"
                              data-testid="cart-item-increase-btn"
                            >
                              <Plus className="w-3 h-3" />
                            </Button>
                          </div>
                          <div className="text-right">
                            <p className="font-heading text-2xl font-bold text-primary" data-testid="cart-item-price">
                              ₹{((item.product.offer_price || item.product.price) * item.quantity).toFixed(2)}
                            </p>
                            <Button
                              onClick={() => removeItem(item.product.id)}
                              variant="ghost"
                              size="sm"
                              className="text-red-500 hover:text-red-600 hover:bg-red-50 mt-1"
                              data-testid="cart-item-remove-btn"
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Remove
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Order Summary */}
              <div className="lg:col-span-1">
                <div className="bg-card rounded-2xl p-6 shadow-soft sticky top-24" data-testid="cart-summary">
                  <h2 className="font-heading text-2xl font-bold mb-6">Order Summary</h2>
                  <div className="space-y-3 mb-6">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Subtotal</span>
                      <span className="font-semibold" data-testid="cart-subtotal">₹{calculateTotal().toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Shipping</span>
                      <span className="font-semibold">Calculated at checkout</span>
                    </div>
                    <div className="border-t border-border pt-3 mt-3">
                      <div className="flex justify-between">
                        <span className="font-heading text-lg font-semibold">Total</span>
                        <span className="font-heading text-2xl font-bold text-primary" data-testid="cart-total">
                          ₹{calculateTotal().toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                  <Button
                    onClick={() => navigate("/checkout")}
                    className="w-full bg-accent text-accent-foreground hover:bg-accent/90 rounded-full py-6 text-lg font-medium"
                    data-testid="cart-checkout-btn"
                  >
                    Proceed to Checkout
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}
